import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
    Calendar, PlusCircle, Search, FileText, ChevronRight, ChevronLeft, 
    ChevronsLeft, ChevronsRight, Filter, Download, Activity, Clock
} from 'lucide-react';
import AsientoManualForm from './AsientoManualForm';
import API_BASE_URL from '../../Config';

const LibroDiario = () => {
    const [asientos, setAsientos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [goToPage, setGoToPage] = useState('1');

    // Default last 30 days
    const [fechaInicio, setFechaInicio] = useState(format(new Date(new Date().setDate(new Date().getDate() - 30)), 'yyyy-MM-dd'));
    const [fechaFin, setFechaFin] = useState(format(new Date(), 'yyyy-MM-dd'));

    const fetchDiario = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE_URL}/contabilidad/diario?inicio=${fechaInicio}&fin=${fechaFin}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAsientos(res.data);
        } catch (error) {
            console.error("Error cargando el libro diario", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDiario();
        setCurrentPage(1);
        setGoToPage('1');
    }, [fechaInicio, fechaFin, rowsPerPage]);

    const totalItems = asientos.length;
    const totalPages = Math.ceil(totalItems / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = Math.min(startIndex + rowsPerPage, totalItems);
    const currentItems = asientos.slice(startIndex, endIndex);

    const handlePageChange = (newPage) => {
        const page = Math.max(1, Math.min(newPage, totalPages));
        setCurrentPage(page);
        setGoToPage(page.toString());
    };

    const handleGoToPage = (e) => {
        if (e.key === 'Enter') {
            const page = parseInt(goToPage);
            if (!isNaN(page)) {
                handlePageChange(page);
            }
        }
    };

    if (showForm) {
        return <AsientoManualForm onBack={() => { setShowForm(false); fetchDiario(); }} />;
    }

    return (
        <div className="bg-slate-50 min-h-screen">
            {/* Header & Controls */}
            <div className="mb-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900 mb-1">Libro Diario General</h1>
                        <p className="text-slate-500 font-medium text-sm">Registro cronológico de asientos contables</p>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center bg-white p-1 rounded-xl border border-slate-200 shadow-sm">
                            <div className="flex items-center gap-2 px-3">
                                <Calendar className="w-4 h-4 text-slate-400" />
                                <input 
                                    type="date" 
                                    value={fechaInicio} 
                                    onChange={e => setFechaInicio(e.target.value)} 
                                    className="text-xs font-bold text-slate-700 outline-none bg-transparent"
                                />
                            </div>
                            <div className="w-px h-4 bg-slate-200 mx-1" />
                            <div className="flex items-center gap-2 px-3">
                                <input 
                                    type="date" 
                                    value={fechaFin} 
                                    onChange={e => setFechaFin(e.target.value)} 
                                    className="text-xs font-bold text-slate-700 outline-none bg-transparent"
                                />
                            </div>
                        </div>

                        <button 
                            className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-all text-xs font-bold shadow-sm" 
                            onClick={() => setShowForm(true)}
                        >
                            <PlusCircle className="w-4 h-4" />
                            Nuevo Asiento
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="bg-white border border-slate-200 shadow-sm overflow-hidden">
                {loading ? (
                    <div className="py-24 flex flex-col items-center justify-center">
                        <div className="animate-spin h-8 w-8 border-[3px] border-blue-600 border-t-transparent rounded-full mb-4"></div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Consolidando diario contable...</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-slate-200 bg-white">
                                    <th className="px-4 py-3 text-[11px] font-black text-slate-400 uppercase tracking-wider w-24 text-center">Asiento</th>
                                    <th className="px-4 py-3 text-[11px] font-black text-slate-400 uppercase tracking-wider w-24 text-center">Fecha</th>
                                    <th className="px-4 py-3 text-[11px] font-black text-slate-400 uppercase tracking-wider">Cuenta / Concepto</th>
                                    <th className="px-4 py-3 text-[11px] font-black text-slate-400 uppercase tracking-wider text-right w-32">Debe</th>
                                    <th className="px-4 py-3 text-[11px] font-black text-slate-400 uppercase tracking-wider text-right w-32">Haber</th>
                                </tr>
                            </thead>
                            <tbody>
                                {asientos.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-4 py-16 text-center">
                                            <div className="flex flex-col items-center gap-2 text-slate-400">
                                                <Search className="w-8 h-8 opacity-20" />
                                                <p className="text-sm font-medium italic">No se encontraron movimientos en este rango.</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    currentItems.map((asiento) => (
                                        <React.Fragment key={asiento.idAsiento}>
                                            <tr className="bg-slate-50/50">
                                                <td className="px-4 py-2 text-center">
                                                    <span className="text-xs font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-100">
                                                        #{asiento.numeroAsiento}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-2 text-center text-xs font-bold text-slate-500">
                                                    {asiento.fechaAsiento}
                                                </td>
                                                <td colSpan="3" className="px-4 py-2">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-sm font-bold text-slate-800">{asiento.glosa}</span>
                                                        <span className="text-[9px] font-black uppercase tracking-tighter text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
                                                            {asiento.moduloOrigen}
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                            {asiento.detalles.map((det, idx) => (
                                                <tr key={idx} className="group hover:bg-slate-50 transition-colors">
                                                    <td className="px-4 py-1.5" colSpan="2"></td>
                                                    <td className="px-4 py-1.5">
                                                        <div className={`flex items-center gap-3 ${det.haber > 0 ? 'ml-8' : ''}`}>
                                                            <span className="text-[10px] font-mono font-bold text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100">
                                                                {det.codigoCuenta}
                                                            </span>
                                                            <span className={`text-sm ${det.haber > 0 ? 'text-slate-600' : 'text-slate-900 font-medium'}`}>
                                                                {det.nombreCuenta}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-1.5 text-right font-mono text-sm font-bold text-slate-700">
                                                        {det.debe > 0 ? new Intl.NumberFormat('es-PY').format(det.debe) : ''}
                                                    </td>
                                                    <td className="px-4 py-1.5 text-right font-mono text-sm font-bold text-slate-700">
                                                        {det.haber > 0 ? new Intl.NumberFormat('es-PY').format(det.haber) : ''}
                                                    </td>
                                                </tr>
                                            ))}
                                            <tr className="border-b border-slate-100 bg-white">
                                                <td colSpan="3" className="px-4 py-2 text-right">
                                                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-300">Suman Gs.</span>
                                                </td>
                                                <td className="px-4 py-2 text-right">
                                                    <div className="text-sm font-black text-slate-900 border-t border-slate-200 pt-1">
                                                        {new Intl.NumberFormat('es-PY').format(asiento.totalDebe)}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-2 text-right">
                                                    <div className="text-sm font-black text-slate-900 border-t border-slate-200 pt-1">
                                                        {new Intl.NumberFormat('es-PY').format(asiento.totalHaber)}
                                                    </div>
                                                </td>
                                            </tr>
                                            <tr className="h-2"><td colSpan="5"></td></tr>
                                        </React.Fragment>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Paginación estilo Premium */}
                {!loading && asientos.length > 0 && (
                    <div className="p-4 bg-white border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500">Ir a</span>
                                <input 
                                    type="text" 
                                    className="w-12 h-8 border border-slate-200 rounded text-center text-xs font-bold outline-none focus:ring-2 focus:ring-blue-500"
                                    value={goToPage}
                                    onChange={(e) => setGoToPage(e.target.value)}
                                    onKeyDown={handleGoToPage}
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500">Ver</span>
                                <select 
                                    className="h-8 border border-slate-200 rounded text-xs font-bold px-2 outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                    value={rowsPerPage}
                                    onChange={(e) => {
                                        setRowsPerPage(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                >
                                    {[5, 10, 20, 50, 100].map(val => (
                                        <option key={val} value={val}>{val}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="flex items-center gap-1">
                            <button 
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${currentPage === 1 ? 'text-slate-300' : 'text-slate-600'}`}
                            >
                                <ChevronsLeft className="w-4 h-4" />
                            </button>
                            <button 
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${currentPage === 1 ? 'text-slate-300' : 'text-slate-600'}`}
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>

                            <div className="flex items-center mx-2 gap-1">
                                {[...Array(totalPages)].map((_, i) => {
                                    const p = i + 1;
                                    if (totalPages > 5) {
                                        if (p !== 1 && p !== totalPages && Math.abs(p - currentPage) > 1) {
                                            if (p === 2 || p === totalPages - 1) return <span key={p} className="text-slate-300">...</span>;
                                            return null;
                                        }
                                    }
                                    return (
                                        <button
                                            key={p}
                                            onClick={() => handlePageChange(p)}
                                            className={`w-8 h-8 rounded text-xs font-bold transition-all ${currentPage === p ? 'bg-blue-600 text-white shadow-md' : 'text-slate-600 hover:bg-slate-100'}`}
                                        >
                                            {p}
                                        </button>
                                    );
                                })}
                            </div>

                            <button 
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${currentPage === totalPages || totalPages === 0 ? 'text-slate-300' : 'text-slate-600'}`}
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                            <button 
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${currentPage === totalPages || totalPages === 0 ? 'text-slate-300' : 'text-slate-600'}`}
                            >
                                <ChevronsRight className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                            [{totalItems > 0 ? startIndex + 1 : 0} a {endIndex} de {totalItems}]
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LibroDiario;
